import { makeAutoObservable, runInAction } from 'mobx'
import React from 'react'
import { type ActionItem } from 'shared/ui'
import { logger } from 'shared/lib'
import { ZIndex } from 'shared/constants/zIndex'
import { Conversation } from 'entities/Conversation'
import { EnumVariantMessageField, MessageFieldStore, EmojiAction } from 'widgets/MessageField'
import { type CallPopUpInsightCardsStore } from 'widgets/CallPopUp/store/callPopUpInsightCardsStore'

type INoteDraft = {
  text: string
  contactId: number
  teamId: number
  sent: boolean
  entryId: string
}

export class CallNoteStore {
  private _messageFieldStore: MessageFieldStore
  private _drafts = new Map<string, INoteDraft>()
  private _activeCallSid: string | null = null
  private _activeEntryId: string | null = null

  constructor(private _callPopUpInsightCardsStore: CallPopUpInsightCardsStore) {
    makeAutoObservable(this)

    this._messageFieldStore = new MessageFieldStore(
      {
        placeholder: 'Write call note…',
        noCounter: true,
        showActionsItems: 1,
        textLimit: {
          maxLength: 1600,
          message: 'Note can contain max 1600 characters',
          showNotification: true,
        },
        variant: EnumVariantMessageField.Note,
      },
      this._callPopUpInsightCardsStore.entities
    )
    this.setActions()
    this._messageFieldStore.setFocusMessageFieldTrigger()
  }

  private _template = 'Call notes'

  setActions = () => {
    this._messageFieldStore?.setActions(this.getMessageFieldActions())
  }

  setFocusMessageField = () => {
    this._messageFieldStore.setFocusMessageFieldTrigger()
  }

  getMessageFieldActions = () => {
    const actions: (ActionItem | null)[] = []

    actions.push({
      iconButtonComponent: <EmojiAction zIndex={ZIndex.OVERLAY_TOP_1} />,
    })

    return actions.filter((action) => !!action) as ActionItem[]
  }

  get entities() {
    return this._callPopUpInsightCardsStore.entities
  }

  get callStore() {
    return this._callPopUpInsightCardsStore.callStore
  }

  get conversationStore() {
    return this._callPopUpInsightCardsStore.conversationStore
  }

  get activeCallSid() {
    return this._activeCallSid
  }

  get activeDraftText() {
    return (this._activeCallSid && this._drafts.get(this._activeCallSid)?.text) || ''
  }

  syncActiveCall = (callSid: string, contactId: number, teamId: number, entryId: string) => {
    if (!callSid || !contactId || !teamId) return

    if (entryId && entryId === this._activeEntryId && callSid !== this._activeCallSid) {
      this._persistFieldToActiveDraft()

      const prevSid = this._activeCallSid
      const draft = prevSid ? this._drafts.get(prevSid) : undefined
      if (prevSid && draft) {
        draft.contactId = contactId
        draft.teamId = teamId
        this._drafts.delete(prevSid)
        this._drafts.set(callSid, draft)
      }

      this._activeCallSid = callSid
      if (!this._drafts.has(callSid)) {
        this._drafts.set(callSid, { text: '', contactId, teamId, sent: false, entryId })
      }

      return
    }

    const existing = this._drafts.get(callSid)
    if (callSid === this._activeCallSid) {
      if (existing) {
        existing.contactId = contactId
        existing.teamId = teamId
        existing.entryId = entryId
      }
      this._activeEntryId = entryId
      return
    }

    const hadActiveCall = !!this._activeCallSid

    this._persistFieldToActiveDraft()
    this._activeCallSid = callSid
    this._activeEntryId = entryId

    let shouldLoadField = true
    if (existing) {
      existing.contactId = contactId
      existing.teamId = teamId
      existing.entryId = entryId
    } else {
      const seedText = hadActiveCall ? '' : this._messageFieldStore.text
      this._drafts.set(callSid, { text: seedText, contactId, teamId, sent: false, entryId })
      if (seedText) shouldLoadField = false
    }

    if (shouldLoadField) this._loadActiveDraftIntoField()
  }

  sendDraftForCallSid = async (callSid: string) => {
    if (!callSid) return

    if (callSid === this._activeCallSid) {
      this._persistFieldToActiveDraft()
    }

    const draft = this._drafts.get(callSid)
    if (!draft || !draft.text || draft.sent) return

    const liveEntry = this.callStore.allCallEntries.find((entry) => entry.id === draft.entryId)
    if (liveEntry) {
      if (liveEntry.callSid !== callSid) {
        const target = this._drafts.get(liveEntry.callSid)
        if (!target || (!target.text && draft.text)) {
          this._drafts.set(liveEntry.callSid, draft)
        }
        this._drafts.delete(callSid)
        if (this._activeCallSid === callSid) {
          this._activeCallSid = liveEntry.callSid
        }
      }
      return
    }

    runInAction(() => {
      draft.sent = true
    })

    try {
      const conversation = await this.conversationStore.createConversation({
        contact_id: draft.contactId,
        team_id: draft.teamId,
      })

      if (!(conversation instanceof Conversation)) {
        runInAction(() => {
          draft.sent = false
        })
        return
      }

      const message = await this.entities.messageStore.createMessageByConversationId(
        conversation.id,
        {
          media_url: [],
          manual_created: true,
          message: `${this._template}\n\n ${draft.text}`,
          send_at: '',
          stop_on_response: false,
          type: 'note',
          should_save_draft: true,
          call_sid: callSid,
          sub_source: null,
        }
      )

      if (!message) {
        runInAction(() => {
          draft.sent = false
        })
        return
      }

      runInAction(() => {
        this._drafts.delete(callSid)
        if (callSid === this._activeCallSid) {
          this._activeCallSid = null
          this._activeEntryId = null
        }
      })
    } catch (e) {
      runInAction(() => {
        draft.sent = false
      })
      logger.error(e)
    }
  }

  private _persistFieldToActiveDraft = () => {
    if (!this._activeCallSid) return
    const draft = this._drafts.get(this._activeCallSid)
    if (!draft || draft.sent) return

    draft.text = this._messageFieldStore.text
  }

  private _loadActiveDraftIntoField = () => {
    this._messageFieldStore.setMessageData({
      attachments: [],
      message: this.activeDraftText,
      isFocus: false,
      isReset: false,
      isReplace: true,
    })
  }

  prepareMessageData = () => {
    this._persistFieldToActiveDraft()
  }

  handleMessageSend = async () => {
    if (!this._activeCallSid) return
    if (this.callStore.callsMap.has(this._activeCallSid)) return

    await this.sendDraftForCallSid(this._activeCallSid)
  }

  setMessageFieldData = () => {
    this._loadActiveDraftIntoField()
  }

  get template() {
    return this._template
  }

  get messageFieldStore() {
    return this._messageFieldStore
  }

  get callPopUpInsightCardsStore() {
    return this._callPopUpInsightCardsStore
  }
}
